/* eslint-disable */

import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
  useRef,
  useCallback,
} from 'react';

import { List, Paper, useTheme } from '@mui/material';

import TicketListItem from '../TicketListItemCustom';
import TicketsListSkeleton from '../TicketsListSkeleton';

import useTickets from '../../hooks/useTickets';
import { i18n } from '../../translate/i18n';
import { AuthContext } from '../../context/Auth/AuthContext';
import { getStyled } from './styles';

const ticketSortAsc = (a, b) =>
  a.updatedAt < b.updatedAt ? -1 : a.updatedAt > b.updatedAt ? 1 : 0;
const ticketSortDesc = (a, b) =>
  a.updatedAt > b.updatedAt ? -1 : a.updatedAt < b.updatedAt ? 1 : 0;

const sortTickets = (list, sortDir) => {
  if (!sortDir || !['ASC', 'DESC'].includes(sortDir)) return list;
  return sortDir === 'ASC'
    ? list.sort(ticketSortAsc)
    : list.sort(ticketSortDesc);
};

const ticketsReducer = (state, action) => {
  const { sortDir } = action;

  switch (action.type) {
    case 'LOAD_TICKETS': {
      const incoming = action.payload;
      const stateMap = new Map(state.map((t) => [t.id, t]));

      incoming.forEach((ticket) => {
        stateMap.set(ticket.id, ticket);
      });

      const merged = Array.from(stateMap.values());

      const withUnread = merged.filter((t) => t.unreadMessages > 0);
      const withoutUnread = merged.filter((t) => !t.unreadMessages);

      return [
        ...sortTickets(withUnread, sortDir),
        ...sortTickets(withoutUnread, sortDir),
      ];
    }

    case 'RESET_UNREAD': {
      const updated = state.map((t) =>
        t.id === action.payload ? { ...t, unreadMessages: 0 } : t,
      );
      return sortTickets(updated, sortDir);
    }

    case 'UPDATE_TICKET': {
      const ticket = action.payload;
      const exists = state.some((t) => t.id === ticket.id);
      const next = exists
        ? state.map((t) => (t.id === ticket.id ? ticket : t))
        : [ticket, ...state];
      return sortTickets(next, sortDir);
    }

    case 'UPDATE_TICKET_UNREAD_MESSAGES': {
      const ticket = action.payload;
      const filtered = state.filter((t) => t.id !== ticket.id);
      return [ticket, ...filtered];
    }

    case 'UPDATE_TICKET_CONTACT': {
      const contact = action.payload;
      return state.map((t) =>
        t.contactId === contact.id ? { ...t, contact } : t,
      );
    }

    case 'DELETE_TICKET': {
      const next = state.filter((t) => t.id !== action.payload);
      return sortTickets(next, sortDir);
    }

    case 'RESET':
      return [];

    default:
      return state;
  }
};

const TicketsListCustom = ({
  setAlertOpen,
  setAlertPending,
  status,
  searchParam,
  searchOnMessages,
  tags,
  users,
  showAll,
  selectedQueueIds,
  updateCount,
  style,
  whatsappIds,
  forceSearch,
  statusFilter,
  userFilter,
}) => {
  const theme = useTheme();
  const classes = getStyled(theme);

  const [pageNumber, setPageNumber] = useState(1);
  const [ticketsList, dispatch] = useReducer(ticketsReducer, []);
  const [fixedTickets, setFixedTickets] = useState([]);

  const { user, socket, isAuth } = useContext(AuthContext);
  const { profile, queues } = user;

  const showTicketWithoutQueue = user.allTicket === 'enable';
  const companyId = user.companyId;

  const filtersRef = useRef({});
  filtersRef.current = {
    showAll,
    selectedQueueIds,
    showTicketWithoutQueue,
    status,
    user,
  };

  useEffect(() => {
    dispatch({ type: 'RESET' });
    setPageNumber(1);
  }, [
    status,
    searchParam,
    showAll,
    tags,
    users,
    forceSearch,
    selectedQueueIds,
    whatsappIds,
    statusFilter,
    searchOnMessages,
  ]);

  const { tickets, hasMore, loading } = useTickets({
    pageNumber,
    searchParam,
    status,
    showAll,
    searchOnMessages: searchOnMessages ? 'true' : 'false',
    tags: JSON.stringify(tags),
    users: JSON.stringify(users),
    queueIds: JSON.stringify(selectedQueueIds),
    whatsappIds: JSON.stringify(whatsappIds),
    statusFilter: JSON.stringify(statusFilter),
    userFilter,
    isAuth,
  });

  useEffect(() => {
    if (!isAuth || !companyId) return;
    dispatch({ type: 'LOAD_TICKETS', payload: tickets, status });
  }, [tickets, isAuth, companyId]);

  useEffect(() => {
    const hasPendingUnread = ticketsList.some(
      (ticket) =>
        ticket.status === 'pending' &&
        ticket.unreadMessages > 0 &&
        ticket.queueId &&
        selectedQueueIds.includes(ticket.queueId),
    );
    setAlertPending(hasPendingUnread);

    const pinned = ticketsList
      .filter((t) => t.pinnedIndex !== null)
      .sort((a, b) => a.pinnedIndex - b.pinnedIndex)
      .map((t) => t.id);

    setFixedTickets(pinned);
  }, [ticketsList, selectedQueueIds, setAlertPending]);

  useEffect(() => {
    if (!socket || typeof socket.on !== 'function') {
      console.warn('Socket não disponível em TicketsListCustom');
      return;
    }

    const shouldUpdateTicket = (ticket) => {
      const { showAll, selectedQueueIds, showTicketWithoutQueue, user } =
        filtersRef.current;
      return (
        (!ticket?.userId || ticket?.userId === user?.id || showAll) &&
        ((!ticket?.queueId && showTicketWithoutQueue) ||
          selectedQueueIds.includes(ticket?.queueId))
      );
    };

    const notBelongsToUserQueues = (ticket) => {
      const { selectedQueueIds } = filtersRef.current;
      return ticket.queueId && !selectedQueueIds.includes(ticket.queueId);
    };

    const onConnect = () => {
      if (typeof socket.emit !== 'function') return;
      status
        ? socket.emit('joinTickets', status)
        : socket.emit('joinNotification');
    };

    const onCompanyTicket = (data) => {
      if (data.action === 'updateUnread') {
        dispatch({ type: 'RESET_UNREAD', payload: data.ticketId, status });
        return;
      }

      if (data.action === 'update') {
        const { status: currentStatus } = filtersRef.current;

        if (
          shouldUpdateTicket(data.ticket) &&
          data.ticket.status === currentStatus
        ) {
          dispatch({ type: 'UPDATE_TICKET', payload: data.ticket, status });
        }

        if (notBelongsToUserQueues(data.ticket)) {
          dispatch({ type: 'DELETE_TICKET', payload: data.ticket?.id, status });
        }
        return;
      }

      if (data.action === 'delete') {
        dispatch({ type: 'DELETE_TICKET', payload: data?.ticketId, status });
      }
    };

    const onAppMessage = (data) => {
      if (
        data.action === 'create' &&
        shouldUpdateTicket(data.ticket) &&
        data.ticket.status === filtersRef.current.status
      ) {
        dispatch({
          type: 'UPDATE_TICKET_UNREAD_MESSAGES',
          payload: data.ticket,
          status,
        });
      }
    };

    const onContact = (data) => {
      if (data.action === 'update' && data.contact) {
        dispatch({
          type: 'UPDATE_TICKET_CONTACT',
          payload: data.contact,
          status,
        });
      }
    };

    socket.on('connect', onConnect);
    socket.on(`company-${companyId}-ticket`, onCompanyTicket);
    socket.on(`company-${companyId}-appMessage`, onAppMessage);
    socket.on(`company-${companyId}-contact`, onContact);

    onConnect();

    return () => {
      if (typeof socket.emit === 'function') {
        status
          ? socket.emit('leaveTickets', status)
          : socket.emit('leaveNotification');
      }
      if (typeof socket.off === 'function') {
        socket.off('connect', onConnect);
        socket.off(`company-${companyId}-ticket`, onCompanyTicket);
        socket.off(`company-${companyId}-appMessage`, onAppMessage);
        socket.off(`company-${companyId}-contact`, onContact);
      }
    };
  }, [socket, companyId, status]);

  useEffect(() => {
    if (typeof updateCount !== 'function') return;

    if (status === 'group') {
      const unreadCount = ticketsList.filter(
        (t) => t.unreadMessages > 0,
      ).length;
      updateCount(unreadCount);
    } else {
      updateCount(ticketsList.length);
    }
  }, [ticketsList, status, updateCount]);

  const loadMore = useCallback(() => {
    setPageNumber((prev) => prev + 1);
  }, []);

  const handleScroll = useCallback(
    (e) => {
      if (!hasMore || loading) return;
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      if (scrollHeight - (scrollTop + 100) < clientHeight) {
        loadMore();
      }
    },
    [hasMore, loading, loadMore],
  );

  const displayTickets = React.useMemo(() => {
    const filtered =
      status && status !== 'search'
        ? ticketsList.filter((t) => t.status === status)
        : ticketsList;

    const pinned = fixedTickets
      .map((id) => filtered.find((t) => t.id === id))
      .filter(Boolean)
      .sort((a, b) => a.pinnedIndex - b.pinnedIndex);

    const unpinned = filtered
      .filter((t) => !fixedTickets.includes(t.id))
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));

    return [...pinned, ...unpinned];
  }, [ticketsList, fixedTickets, status]);

  return (
    <Paper style={{ ...classes.ticketsListWrapper, ...style }}>
      <Paper
        square
        elevation={0}
        style={classes.ticketsList}
        onScroll={handleScroll}
      >
        <List style={{ paddingTop: 0, paddingRight: '12px' }}>
          {displayTickets.length === 0 && !loading ? (
            <div style={classes.noTicketsDiv}>
              <span style={classes.noTicketsTitle}>
                {i18n.t('ticketsList.noTicketsTitle')}
              </span>
              <p style={classes.noTicketsText}>
                {i18n.t('ticketsList.noTicketsMessage')}
              </p>
            </div>
          ) : (
            <>
              {displayTickets.map((ticket) => (
                <TicketListItem
                  ticket={ticket}
                  key={`${ticket.id}-${fixedTickets.includes(ticket.id) ? 'fixed' : 'normal'}-${ticket.updatedAt}`}
                  setAlertOpen={setAlertOpen}
                  fixedList={fixedTickets}
                />
              ))}
            </>
          )}
          {loading && <TicketsListSkeleton />}
        </List>
      </Paper>
    </Paper>
  );
};

export default TicketsListCustom;
